'use client'

import { useRef, useState } from 'react'
import { buildIntentUrl } from '@/lib/shareUrl'
import { useBodyScrollLock } from '@/lib/useBodyScrollLock'
import { useFocusTrap } from '@/lib/useFocusTrap'

const PLATFORMS = [
  { id: 'line', label: 'LINE', icon: '💬' },
  { id: 'whatsapp', label: 'WhatsApp', icon: '💚' },
  { id: 'facebook', label: 'Facebook', icon: '📘' },
  { id: 'telegram', label: 'Telegram', icon: '✈️' },
  { id: 'threads', label: 'Threads', icon: '🧵' },
  { id: 'x', label: 'X', icon: '𝕏' },
  { id: 'email', label: 'Email', icon: '✉️' },
]

export default function ShareSheet({ open, onClose, payload, placement = 'card' }) {
  const panelRef = useRef(null)
  const [copied, setCopied] = useState(false)

  useBodyScrollLock(open)
  useFocusTrap({ isActive: open, containerRef: panelRef, onEscape: onClose })

  if (!open) return null

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payload.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
      prompt('請手動複製連結', payload.url)
    }
  }

  const handlePlatform = (platform) => {
    const intentUrl = buildIntentUrl({ platform, shareUrl: payload.url, title: payload.title })
    window.open(intentUrl, '_blank', 'noopener,noreferrer')
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="分享選項"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
      <div
        ref={panelRef}
        className="relative w-full md:w-[320px] max-w-full rounded-t-[16px] md:rounded-[12px]"
        style={{
          backgroundColor: 'var(--color-bg)',
          boxShadow: '0 -4px 30px rgba(0,0,0,0.15)',
          paddingBottom: 'var(--safe-area-bottom, 12px)',
        }}
      >
        <div
          className="px-5 py-3 text-sm font-medium"
          style={{ color: 'var(--color-text)', borderBottom: '1px solid var(--color-cardBorder)' }}
        >
          分享這張圖
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="w-full flex items-center gap-3 px-5 py-3 text-sm text-left transition-colors hover:bg-black/5"
          style={{ color: 'var(--color-text)', minHeight: '44px' }}
        >
          <span aria-hidden="true">📋</span>
          <span>{copied ? '已複製 ✓' : '複製連結'}</span>
        </button>
        {PLATFORMS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => handlePlatform(p.id)}
            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-left transition-colors hover:bg-black/5"
            style={{ color: 'var(--color-text)', minHeight: '44px' }}
          >
            <span aria-hidden="true">{p.icon}</span>
            <span>{p.label}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={onClose}
          className="w-full px-5 py-3 text-sm text-center"
          style={{
            color: 'var(--color-textMuted)',
            borderTop: '1px solid var(--color-cardBorder)',
            minHeight: '44px',
          }}
        >
          取消
        </button>
      </div>
    </div>
  )
}
